import React from 'react';
import { Page, View, Text, Image, Document, StyleSheet } from '@react-pdf/renderer';
import dayjs from "dayjs";
import { OrderStatusEnum } from "../../utils/enums/OrderStatusEnum";
import InvoiceQR from "./InvoiceQR";
import InvoiceSellerBlock from "./InvoiceSellerBlock";
import { orderDeepLink } from "../../utils/orderQr";
import { imageSrc } from "./invoiceImageSrc";
import { lineAmount } from "../../utils/orderLine";
import { getOrderStockCategory, orderPlacementSummary } from "../../utils/orderStock";
import "./registerInvoiceFonts";

const money = (n) => 'Tk ' + Math.round(Number(n) || 0).toLocaleString('en-US');
const fmtDate = (d) => (d ? dayjs(d).format('DD MMMM YYYY') : '—');

// The image grid. Four columns of a 511.28pt content width with 6pt gutters
// gives 123.3pt cells; see `imgCell` for why the height is 86.
const IMAGE_COLUMNS = 4;
const IMAGE_GAP = 6;
const IMAGE_CELL_HEIGHT = 86;

/** Split a list into fixed-size rows, so each row can be laid out as a unit. */
const inRowsOf = (items, size) => {
  const rows = [];
  for (let i = 0; i < items.length; i += size) rows.push(items.slice(i, i + size));
  return rows;
};

// The number a customer rings when something is wrong with the parcel — the
// one thing on the sheet that has to be findable without reading it. It is not
// the company profile's `phone`: that is the office line printed in the
// letterhead, and support is answered somewhere else. Fixed here rather than in
// the profile because there is one of it and it is not per-install; if a second
// install ever needs its own, it moves to CompanySetting beside `phone`.
export const CUSTOMER_CARE_PHONE = '+8801906299509';

// `courier` is the one colour on the sheet, and it marks exactly one thing: the
// consignment id. That is the number quoted to the courier's support when
// something has gone wrong, and it sits among identically-shaped digits (order
// no., dates, amounts, and the tracking code right beside it). A dark green
// rather than a bright one: it still separates on a colour laser, and on the
// mono printers the floor actually uses it falls to a mid grey that the bold
// weight keeps legible.
const C = { text: '#1a1a1a', muted: '#6b6b6b', line: '#ececec', lineStrong: '#d8d8d8', headBg: '#f2f2f2', imgBg: '#fafafa', courier: '#0b7a3b' };

// Sizes are in points (react-pdf's default unit), tuned to A4 density.
const s = StyleSheet.create({
  // paddingBottom leaves room for the fixed footer, which is out of the flow and
  // would otherwise be printed over.
  page: { fontFamily: 'CourierPrime', color: C.text, fontSize: 9.5, lineHeight: 1.4, paddingTop: 34, paddingBottom: 52, paddingHorizontal: 42 },

  // Applied to any field that can carry Bangla — Courier Prime has no Bengali, so
  // these render in Hind Siliguri (which also covers Latin/digits fine).
  bangla: { fontFamily: 'HindSiliguri' },

  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' },
  brandRow: { flexDirection: 'row' },
  logo: { width: 54, height: 54, borderRadius: 7, marginRight: 11, objectFit: 'contain' },
  brandName: { fontSize: 12.5, fontWeight: 700, letterSpacing: 0.5 },
  brandMuted: { fontSize: 8.5, color: C.muted, marginTop: 2 },

  invoiceTitle: { fontSize: 20, fontWeight: 700, letterSpacing: 1, marginBottom: 7, textAlign: 'right' },
  metaRow: { flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 2 },
  metaLabel: { width: 88, textAlign: 'right', paddingRight: 14, fontSize: 9, color: C.muted, fontWeight: 700 },
  metaValue: { width: 108, textAlign: 'right', fontSize: 9 },

  rule: { height: 2, backgroundColor: C.text, marginTop: 16 },

  sectionHead: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, paddingBottom: 4, borderBottomWidth: 1, borderBottomColor: C.lineStrong },

  // 4pt, not 5. A point off each row is invisible on a 9.5pt line and buys back
  // 2pt across the seven of them, which is what lets the picture cells stay at
  // 86 rather than 79 now that the courier references take a row each.
  infoRow: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: C.line, paddingVertical: 4 },
  infoLabel: { width: 120, color: C.muted, fontWeight: 700 },
  infoValue: { flex: 1 },

  th: { paddingVertical: 6, paddingHorizontal: 7, fontSize: 8.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 },
  td: { paddingVertical: 6, paddingHorizontal: 7, fontSize: 9 },
  readyStockTag: { fontSize: 7, fontWeight: 700, color: C.courier, marginTop: 2 },

  // Prominent header stamps for Ready Stock / Partial Stock orders
  stockBadge: {
    marginTop: 3,
    paddingVertical: 2,
    paddingHorizontal: 4,
    borderRadius: 3,
    borderWidth: 1.2,
    alignItems: 'center',
    width: '100%',
  },
  stockBadgeReady: {
    borderColor: C.courier,
    backgroundColor: '#f0fdf4',
  },
  stockBadgePartial: {
    borderColor: '#d97706',
    backgroundColor: '#fffbeb',
  },
  stockBadgeTextReady: {
    fontSize: 6.5,
    fontWeight: 700,
    color: C.courier,
    textAlign: 'center',
    letterSpacing: 0.3,
  },
  stockBadgeTextPartial: {
    fontSize: 6.5,
    fontWeight: 700,
    color: '#b45309',
    textAlign: 'center',
    letterSpacing: 0.3,
  },
  stockBadgeLoc: {
    fontSize: 5.5,
    color: '#4b5563',
    textAlign: 'center',
    marginTop: 1,
  },

  payRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 3.5 },

  // The consignment id, and only it. Bold does the work on a mono printer, the
  // green on a colour one — neither alone is enough, because the floor prints
  // on both. No size change: a larger line here would move the page boundary
  // that invoiceLayout.test.js pins.
  cid: { fontWeight: 700, color: C.courier },

  // A cell is 123.3 x 86pt — near square, so a portrait phone photo and a
  // landscape flat-lay both fit whole under 'contain' (65x86 and 123x86
  // respectively) with nothing cropped. It used to be 150pt tall in a 123pt
  // column, which is why only four images ever reached page one.
  //
  // Height is the number the page budget turns on: at 86pt, eight images share
  // page one with the details for any order up to five product lines — 99% of
  // the orders in the table, and none of them holds more than seven images.
  //
  // It was 92 until the courier references were split onto a row each. That row
  // costs ~24pt, and the picture grid is where the budget had to find it: 87
  // already spills, so the threshold is exact rather than chosen. Trimming a
  // point off `infoRow` first is what kept it at 86 instead of 79 — the same
  // invariant, for a 6% smaller photo rather than a 14% one. The row owns the
  // vertical gap, so no margin here.
  imgCell: { height: IMAGE_CELL_HEIGHT, borderRadius: 6, overflow: 'hidden', borderWidth: 1, borderColor: C.line, backgroundColor: C.imgBg },

  // Fixed, not flowed. As the last element in the flow this footer produced a
  // blank trailing page whenever the content happened to end within a few
  // points of the bottom edge: react-pdf opened a page for it, then found
  // nothing to paint. Anchored to the page frame it cannot straddle the
  // boundary at all — and it now signs every page of a long invoice, not just
  // the last. `left`/`right` mirror the page's paddingHorizontal.
  // A row, not a stack. The care number and the sign-off sit at opposite ends
  // of one line because a second line would grow the footer past the page's
  // 52pt bottom padding and print over the last row of pictures.
  footer: {
    position: 'absolute', bottom: 18, left: 42, right: 42,
    flexDirection: 'row', justifyContent: 'space-between',
    fontSize: 9, color: '#8a8a8a',
    borderTopWidth: 1, borderTopColor: C.line, paddingTop: 10,
  },

  // Darker and bold against the grey beside it. This is a number someone reads
  // off a printed sheet under pressure, and at 9pt the footer grey is the
  // faintest ink on the page.
  footerCare: { color: C.muted, fontWeight: 700 },
});

// Hind Siliguri sits lower than Courier Prime on the same line, and the two are
// used side by side in every label/value row and in every product line.
//
// react-pdf puts a run's baseline at `ascent x fontSize` below the top of its
// text box and ignores lineHeight in doing so — visible in the content stream as
// a `1 0 0 1 0 <ascent x size> cm` immediately before each `BT`. The two fonts
// disagree about ascent by a lot: Courier Prime is 1600/2048 em, Hind Siliguri
// 1116/1000, so Bengali text drops 0.335em — 3.2pt at the 9.5pt body size, which
// is what made "Delivery Channel" and its value look like two different rows.
//
// No flex alignment can fix it: with `lineHeight` set, both boxes are exactly
// the same height, so the difference is entirely *inside* the box, and
// `alignItems: 'baseline'` is a no-op here — react-pdf's yoga nodes expose no
// baseline function, which was checked before this constant was written.
//
// So the run is lifted back by the difference. `top` rather than `marginTop`:
// relative positioning shifts the glyphs without changing the row's height, so
// the page budget is untouched.
export const BANGLA_ASCENT_LIFT = 1116 / 1000 - 1600 / 2048;

/**
 * The Bengali-capable font, aligned to the Courier Prime beside it.
 *
 * Takes the size it will render at because the lift is proportional to it, and
 * react-pdf gives a style no way to read its own inherited font size. Omit the
 * argument for a run with no Courier Prime alongside it — there is nothing to
 * align to, and lifting it would only move it off its own line.
 */
const bangla = (fontSize) => {
  if (!fontSize) return s.bangla;

  const lift = BANGLA_ASCENT_LIFT * fontSize;

  // Paired, so the run's outer height is unchanged and no row, block or page
  // boundary moves — only the glyphs do. `top` looked like the tidier lever and
  // is not: react-pdf renders a relative offset here into a degenerate first
  // page, which the page-budget suite catches on a four-line order.
  return { ...s.bangla, marginTop: -lift, marginBottom: lift };
};

// Five columns, and the widths are a budget rather than a preference: at five
// lines and eight images the sheet is one page, and a row that grows a line is
// a second one. invoiceLayout.test.js pins that.
//
// Fabric carries four terms now — fabric, print, colour and cut — and took its
// extra width from Description, the one free-text column that can lose 8%
// without running to more lines.
//
// The money column is the line's amount, not the price of one, because it has
// to add up to the Payment Subtotal beneath it — that is the number a customer
// checks. Printing both, with a Unit column beside it, is the better sheet and
// costs a photo-heavy order a page; measured, then given up. The unit price is
// on the order page and in the app.
const COL = { product: '18%', fabric: '26%', desc: '32%', qty: '10%', amount: '14%' };

const SectionHead = ({ children, style }) => <Text style={[s.sectionHead, style]}>{children}</Text>;

/**
 * What one product line is made of, as the Fabric cell prints it: fabric,
 * print, colour, cut.
 *
 * The print is what the cutting floor needs in order to pull the right cloth —
 * a fabric's book runs to hundreds of prints, and "print-142" cannot be
 * inferred from the colour beside it.
 *
 * It reads directly after the fabric, and before the colour, because that is
 * the order the cloth is chosen in: a print is picked off one fabric's book,
 * and the colour is picked within it. The order form, the store table and the
 * receipt export all now name the two that way round, and an invoice that
 * disagreed would be the one place the floor had to read backwards.
 *
 * A line placed before the pattern book existed has no print, and drops the
 * term rather than printing a gap.
 */
export const fabricSummary = (product) => [
  product.fabricType?.name,
  product.fabricPattern?.name,
  product.fabrics?.name,
  product.productFormation?.name,
].filter(Boolean).join(' · ') || 'N/A';

/**
 * Ready stock placement (location and/or bag), if assigned.
 *
 * Joins location name and bag with " · " if both exist (e.g. "Shelf A · B-12"),
 * or returns whichever is present.
 */
export const stockPlacementSummary = (product) => {
  const loc = product?.stockLocation || product?.location;
  const bag = product?.stockBag || product?.bag || product?.stockBin || product?.bin;
  return [loc, bag].filter(Boolean).join(' · ') || null;
};

/**
 * Ready stock availability label for a product line, if any.
 *
 * If the line was fulfilled from ready stock, indicates that so the floor
 * knows it is already pulled. If ready stock is available on-hand, indicates
 * the available ready stock quantity so the fulfiller can pull it from ready stock.
 * Includes placement/location if available (e.g. "[Ready Stock: 5 @ Shelf A]").
 */
export const readyStockSummary = (product) => {
  const placement = stockPlacementSummary(product);
  const locPart = placement ? ` @ ${placement}` : '';

  if (product?.fulfilledFromStock) {
    return `[From Ready Stock${locPart}]`;
  }
  const available = Number(product?.availableInStock) || 0;
  if (available > 0) {
    return `[Ready Stock: ${available}${locPart}]`;
  }
  return null;
};

/**
 * The rows of the "Customer Information" block, in print order.
 *
 * One block for both channels, because both channels describe a Customer. This
 * used to fork on the channel and read the offline half out of the polymorphic
 * `orderable`, which nothing has written since merchants and customers were
 * unified — so an offline invoice printed a "Merchant Information" heading over
 * two empty rows: no name, no phone, no address, on the sheet that travels with
 * the parcel. The `orderable` name survives only as a fallback, for rows the
 * unification's backfill did not reach.
 *
 * Three rows, not four. A fourth is what pushes the last picture row of an
 * eight-image order onto page two — measured, and so is a sixth row of header
 * meta, the header being as tall as its tallest column. So the reference an
 * offline order carries (in the field an online one uses for the buyer's
 * Facebook) stays off the sheet rather than costing every photo-heavy order a
 * page; it is on the order page, which is where it gets looked up.
 *
 * Exported so the rules above can be asserted without reading them back out of
 * a rendered PDF, where the subset fonts make text unrecoverable.
 */
export const customerBlock = (order, fullAddress) => [
  {
    label: 'Customer Name',
    value: order.customer?.name || order.orderable?.name || '—',
    bengali: true,
  },
  {
    label: 'Phone',
    value: [order.customer?.phone || order.customer?.address?.phone, order.customer?.altPhone]
      .filter(Boolean).join(', ') || '—',
  },
  { label: 'Address', value: fullAddress || '—', bengali: true },
];

/**
 * Who keyed the order in, for the header's "Ordered By".
 *
 * The row used to repeat the customer's name, which the Customer Information
 * block underneath already prints in full with their phone and address. What it
 * did not say anywhere was who in the shop took the order — the person the
 * floor and the courier desk actually need when a sheet raises a question, and
 * the one fact on it that cannot be recovered from the parcel.
 *
 * `submittedBy` comes from the order detail and the bulk-print batch alike. A
 * payload without it — anything cached from before the field shipped — prints a
 * dash rather than falling back to the customer, because a name in this row
 * that means two different things depending on the payload's age is worse than
 * no name at all.
 */
export const submittedBy = (order) => order.submittedBy || '—';

/**
 * One order's invoice, as a single <Page>.
 *
 * Exported apart from the Document below because react-pdf renders one Document
 * per job: a bulk print is many Pages in one Document, not many Documents. The
 * Document wrapper stays the default export so the order page's single-order
 * download is unchanged.
 */
export const OrderInvoicePage = ({ order, company }) => {
  const statusLabel = OrderStatusEnum.find(x => x.value === order.status)?.label || '';
  const products = order.products || [];
  // A row each, and only for the numbers that exist. Built as a list rather
  // than two conditional blocks so the "last row closes the block" rule has
  // something to be true about — either can be the last one.
  const courierRows = [
    order.consignment?.trackingCode
      ? { label: 'Booking No.', value: order.consignment.trackingCode }
      : null,
    order.consignment?.consignmentId
      ? { label: 'Consignment ID', value: String(order.consignment.consignmentId), style: s.cid }
      : null,
  ].filter(Boolean);
  const hasCourierRef = courierRows.length > 0;
  const images = order.images || [];
  const addr = order.customer?.address;
  const fullAddress = addr
    ? [addr.address, addr.upazila?.name, addr.district?.name, addr.division?.name].filter(Boolean).join(', ')
    : '';

  const customerRows = customerBlock(order, fullAddress);

  const stockCategory = getOrderStockCategory(order);
  const placement = orderPlacementSummary(order);

  const meta = [
    { label: 'Order no.', value: order.clientOrderNumber ? `#${order.id} (${order.clientOrderNumber})` : `#${order.id}`, bold: true },
    { label: 'Ordered By', value: submittedBy(order), bangla: true },
    // The team that owns the order (ADR 0001) — both list and detail send it as a name.
    { label: 'Team', value: order.team || '—', bangla: true },
    { label: 'Order Issued', value: fmtDate(order.createdAt) },
    { label: 'Status', value: statusLabel, bold: true },
  ];

  return (
    <Page size="A4" style={s.page}>

      {/* Header */}
      <View style={s.headerRow}>
        <InvoiceSellerBlock company={company} styles={s} />
        <View style={{ width: 84, alignItems: 'center', marginHorizontal: 20, paddingTop: 2 }}>
          <InvoiceQR value={orderDeepLink(order.id)} size={56} />
          <Text style={[bangla(), { fontSize: 6, color: '#8a8a8a', marginTop: 2, textAlign: 'center' }]}>Scan to update status</Text>
          {stockCategory === 'FULL' && (
            <View style={[s.stockBadge, s.stockBadgeReady]}>
              <Text style={s.stockBadgeTextReady}>★ READY STOCK</Text>
              {placement ? <Text style={s.stockBadgeLoc}>@{placement}</Text> : null}
            </View>
          )}
          {stockCategory === 'PARTIAL' && (
            <View style={[s.stockBadge, s.stockBadgePartial]}>
              <Text style={s.stockBadgeTextPartial}>⚠ PARTIAL STOCK</Text>
              {placement ? <Text style={s.stockBadgeLoc}>@{placement}</Text> : null}
            </View>
          )}
        </View>
        <View style={{ flex: 1, alignItems: 'flex-end' }}>
          <Text style={s.invoiceTitle}>INVOICE</Text>
          {meta.map((m, i) => (
            <View key={i} style={s.metaRow}>
              <Text style={s.metaLabel}>{m.label}</Text>
              <Text style={[s.metaValue, m.bold && { fontWeight: 700 }, m.bangla && bangla(9)]}>{m.value}</Text>
            </View>
          ))}
        </View>
      </View>

      <View style={s.rule} />

      {/* Customer */}
      <View style={{ marginTop: 13 }}>
        <SectionHead>Customer Information</SectionHead>
        {customerRows.map(({ label, value, bengali }, i) => (
          <View
            key={label}
            style={[s.infoRow, i === customerRows.length - 1 ? { borderBottomWidth: 0 } : {}]}
          >
            <Text style={s.infoLabel}>{label}</Text>
            <Text style={[s.infoValue, bengali && bangla(9.5)]}>{value}</Text>
          </View>
        ))}
      </View>

      {/* Products */}
      <View style={{ marginTop: 13 }}>
        <SectionHead>Product Information</SectionHead>
        <View style={{ flexDirection: 'row', backgroundColor: C.headBg, marginTop: 8 }}>
          <Text style={[s.th, { width: COL.product }]}>Product</Text>
          <Text style={[s.th, { width: COL.fabric }]}>Fabric</Text>
          <Text style={[s.th, { width: COL.desc }]}>Description</Text>
          <Text style={[s.th, { width: COL.qty, textAlign: 'center' }]}>Qty</Text>
          <Text style={[s.th, { width: COL.amount, textAlign: 'right' }]}>Amount</Text>
        </View>
        {products.map((p, i) => {
          const stock = readyStockSummary(p);
          return (
            <View key={p.id || i} wrap={false} style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: C.line }}>
              <View style={[s.td, { width: COL.product }]}>
                <Text style={[{ fontSize: 9 }, bangla(9)]}>{p.productType?.name}</Text>
                {stock && <Text style={s.readyStockTag}>{stock}</Text>}
              </View>
              <Text style={[s.td, bangla(9), { width: COL.fabric }]}>{fabricSummary(p)}</Text>
              <Text style={[s.td, bangla(9), { width: COL.desc }]}>{p.description}</Text>
              <Text style={[s.td, { width: COL.qty, textAlign: 'center' }]}>{p.unit} pc</Text>
              <Text style={[s.td, { width: COL.amount, textAlign: 'right' }]}>{money(lineAmount(p))}</Text>
            </View>
          );
        })}
      </View>

      {/* Delivery + Payment — kept together on one page */}
      <View wrap={false} style={{ flexDirection: 'row', marginTop: 13 }}>
        <View style={{ flex: 1, marginRight: 36 }}>
          <SectionHead>Delivery Information</SectionHead>
          <View style={s.infoRow}>
            <Text style={[s.infoLabel, { width: 100 }]}>Delivery Date</Text>
            <Text style={s.infoValue}>{fmtDate(order.deliveryDate)}</Text>
          </View>
          <View style={[s.infoRow, hasCourierRef ? {} : { borderBottomWidth: 0 }]}>
            <Text style={[s.infoLabel, { width: 100 }]}>Delivery Channel</Text>
            <Text style={[s.infoValue, bangla(9.5)]}>{order.deliveryChannel?.name || '—'}</Text>
          </View>
          {/* The two courier references, a row each. They answer different
              questions: the tracking code is what a customer is quoted and what
              identifies the parcel; the consignment id is the courier's own
              record number, and the one to quote to their support. Only the
              consignment id is set in colour.

              A row is absent entirely when its number is missing, rather than
              printed empty — a blank "Booking No." on a printed invoice invites
              someone to write the wrong thing in it. Which row closes the block
              therefore depends on what is present, hence `last`. */}
          {courierRows.map(({ label, value, style }, i) => (
            <View
              key={label}
              style={[s.infoRow, i === courierRows.length - 1 ? { borderBottomWidth: 0 } : {}]}
            >
              <Text style={[s.infoLabel, { width: 100 }]}>{label}</Text>
              <Text style={[s.infoValue, style]}>{value}</Text>
            </View>
          ))}
        </View>
        <View style={{ width: 230 }}>
          <SectionHead>Payment Information</SectionHead>
          <View style={s.payRow}>
            <Text style={{ color: C.muted }}>Payment Subtotal</Text>
            <Text>{money(order.payment?.amount)}</Text>
          </View>
          <View style={s.payRow}>
            <Text style={{ color: C.muted }}>Delivery Charge</Text>
            <Text>{money(order.payment?.deliveryCharge)}</Text>
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingTop: 6, marginTop: 2, borderTopWidth: 1.5, borderTopColor: C.text }}>
            <Text style={{ fontWeight: 700 }}>Total Amount</Text>
            <Text style={{ fontWeight: 700, fontSize: 12 }}>{money(order.payment?.totalAmount)}</Text>
          </View>
        </View>
      </View>

      {/* Order images.

          Laid out as explicit rows rather than one flexWrap container, for two
          reasons. A row marked wrap={false} is atomic — it moves to the next
          page whole instead of being sliced through the middle of a picture.
          And the columns are flex:1 with real gutters, where the widths used to
          be percentages that summed to exactly 100.0% (23.5x4 + 2x3), so a
          single rounding up would drop a cell onto a line of its own. */}
      {images.length > 0 && (
        <View style={{ marginTop: 13 }}>
          <SectionHead style={{ marginBottom: 8 }}>Order Images</SectionHead>
          {inRowsOf(images, IMAGE_COLUMNS).map((row, r, rows) => (
            <View
              key={r}
              wrap={false}
              style={{ flexDirection: 'row', marginBottom: r === rows.length - 1 ? 0 : IMAGE_GAP }}
            >
              {row.map((img, k) => (
                <View
                  key={img.id || k}
                  style={[s.imgCell, { flex: 1, marginRight: k === IMAGE_COLUMNS - 1 ? 0 : IMAGE_GAP }]}
                >
                  <Image src={imageSrc(img)} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </View>
              ))}
              {/* A short last row is padded with empty columns, so its pictures
                  keep the grid's width instead of stretching across the page. */}
              {Array.from({ length: IMAGE_COLUMNS - row.length }, (_, k) => (
                <View
                  key={`pad-${k}`}
                  style={{ flex: 1, marginRight: k === IMAGE_COLUMNS - row.length - 1 ? 0 : IMAGE_GAP }}
                />
              ))}
            </View>
          ))}
        </View>
      )}

      {/* Fixed, so it signs every page — the care number included. A customer
          who is holding page two of a long invoice is exactly the one who needs
          the number, and would otherwise have to go looking for page one. */}
      <View fixed style={s.footer}>
        <Text style={s.footerCare}>Customer care: {CUSTOMER_CARE_PHONE}</Text>
        <Text>Thank you for your order.</Text>
      </View>
    </Page>
  );
};

const OrderInvoice = ({ order, company }) => (
  <Document>
    <OrderInvoicePage order={order} company={company} />
  </Document>
);

export default OrderInvoice;
